import React, { useState } from 'react';
import { DotsThreeIcon } from '@phosphor-icons/react';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { Close, DoneAllSharp, PersonAdd } from '@mui/icons-material';
import InitialsAvatar from './InitialsAvatar';
import { font } from '../../utils/fontHelper';

export interface UserListingCardMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
}

interface UserListingCardProps {
  id: string;
  name: string;
  username: string;
  avatar: string | undefined;
  stats: {
    followers: number;
    watchlists: number;
  };
  isFollowing?: boolean;
  isCurrentUser?: boolean;
  showFollowButton?: boolean;
  onUserClick?: (userId: string) => void;
  onFollowToggle?: (userId: string) => Promise<void>;
  size?: 'default' | 'compact';
  onDismiss?: (userId: string) => void;
  /** Vertical alignment for the dismiss control when `onDismiss` is set */
  dismissIconAlign?: 'top' | 'center';
  /** Context menu items shown in the 3-dot menu */
  menuItems?: UserListingCardMenuItem[];
}

const UserListingCard: React.FC<UserListingCardProps> = ({
  id,
  name,
  username,
  avatar,
  stats: _stats,
  isFollowing = false,
  isCurrentUser = false,
  showFollowButton = true,
  onUserClick,
  onFollowToggle,
  size = 'default',
  onDismiss,
  dismissIconAlign = 'top',
  menuItems,
}) => {
  const isDark = useTheme().palette.mode === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isCompact = size === 'compact';
  const cardHeight = isCompact ? 60 : 72;
  const avatarSize = isCompact ? 42 : 48;

  const handleFollowClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onFollowToggle || isCurrentUser || isLoading) return;

    setIsLoading(true);
    try {
      await onFollowToggle(id);
    } catch (error) {
      console.error('Error toggling follow status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserClick = () => {
    if (onUserClick) {
      onUserClick(id);
    }
  };

  const getCardBackground = () => {
    return isDark ? '#2C2C2E' : '#F0EFF0';
  };

  const getActionButtonBackground = () => {
    if (isFollowing) {
      return isDark ? 'rgba(48, 209, 88, 0.7)' : 'rgba(52, 199, 89, 0.7)';
    }
    return isDark ? 'rgba(175, 174, 243, 0.2)' : 'rgba(88, 86, 214, 0.2)';
  };

  const getActionButtonIconColor = () => {
    return isDark ? 'rgba(3, 3, 3, 0.7)' : 'rgba(255, 255, 255, 0.7)';
  };

  const getPrimaryTextColor = () => {
    return isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)';
  };

  const getSecondaryTextColor = () => {
    return isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)';
  };

  return (
    <Box
      onClick={handleUserClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isCompact ? 1.5 : 2,
        pl: onDismiss ? 4 : isCompact ? 1 : 1.25,
        pr: !isCurrentUser && showFollowButton ? 4 : isCompact ? 1 : 1.25,
        py: 0,
        minHeight: cardHeight,
        height: 'auto',
        bgcolor: getCardBackground(),
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        '&:hover': {
          bgcolor: isDark ? '#3A3A3C' : '#E8E8ED',
        },
        position: 'relative',
      }}
    >
      {onDismiss && (
        <IconButton
          size="small"
          aria-label="Dismiss suggestion"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(id);
          }}
          sx={{
            position: 'absolute',
            left: 4,
            zIndex: 2,
            ...(dismissIconAlign === 'center'
              ? {
                  top: '50%',
                  transform: 'translateY(-50%)',
                }
              : {
                  top: 4,
                }),
            color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
            '&:hover': {
              color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)',
              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      {!isCurrentUser && showFollowButton && (
        <Box
          onClick={handleFollowClick}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 2,
            width: 29,
            height: 34,
            bgcolor: getActionButtonBackground(),
            borderRadius: '0px 8px 0px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': { opacity: 0.85 },
          }}
        >
          {isLoading ? (
            <CircularProgress size={16} sx={{ color: getActionButtonIconColor() }} />
          ) : isFollowing ? (
            <DoneAllSharp sx={{ fontSize: 16, color: getActionButtonIconColor() }} />
          ) : (
            <PersonAdd sx={{ fontSize: 16, color: getActionButtonIconColor() }} />
          )}
        </Box>
      )}

      {menuItems && menuItems.length > 0 && (
        <>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
            sx={{
              position: 'absolute',
              right: 4,
              bottom: 4,
              zIndex: 2,
              color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)',
              '&:hover': { color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)', bgcolor: 'transparent' },
            }}
          >
            <DotsThreeIcon size={20}  weight="bold" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              sx: {
                borderRadius: 2,
                bgcolor: isDark ? '#2C2C2E' : '#FFFFFF',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                minWidth: 160,
              }
            }}
          >
            {menuItems.map((item, i) => (
              <MenuItem
                key={i}
                onClick={() => { setMenuAnchor(null); item.onClick(); }}
                sx={{
                  fontFamily: font(),
                  fontSize: 14,
                  gap: 1.5,
                  color: item.destructive ? 'error.main' : 'text.primary',
                  py: 1.25,
                }}
              >
                {item.icon}
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <Box sx={{ position: 'relative' }}>
        <InitialsAvatar
          src={avatar}
          name={name}
          size={avatarSize}
          sx={{
            opacity: 0.8,
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          py: isCompact ? 1 : 1.5,
          minWidth: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: font(),
            fontWeight: 400,
            fontSize: isCompact ? 15 : 16,
            lineHeight: isCompact ? 1.4 : 1.5,
            color: getPrimaryTextColor(),
            cursor: 'pointer',
            '&:hover': {
              color: '#7D7AFF',
            },
          }}
        >
          {name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontFamily: font(),
            fontWeight: 400,
            fontSize: isCompact ? 11 : 12,
            lineHeight: isCompact ? 1.3 : 1.33,
            color: getSecondaryTextColor(),
          }}
        >
          {username}
        </Typography>
      </Box>
    </Box>
  );
};

export default UserListingCard;
